import React, { useEffect, useRef, useState } from "react";
import {
  Bold,
  Italic,
  Underline,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Minus,
} from "lucide-react";

/*
 * Lightweight contentEditable rich-text editor.
 * - No external deps.
 * - Emits sanitized-friendly HTML via onChange.
 * - Toolbar: bold, italic, underline, H2, H3, ul, ol, blockquote, link, hr.
 * - Admin-only surface — backend re-sanitizes on save (`cms.py::sanitize_html`).
 */
export default function RichTextEditor({
  value,
  onChange,
  placeholder = "Start writing…",
  minHeight = 180,
  testId,
}) {
  const ref = useRef(null);
  const [isEmpty, setIsEmpty] = useState(!value);

  // Populate initial HTML once. We deliberately do NOT sync on every value
  // change to avoid caret jump — parent re-mounts by key when it needs a full
  // reset (e.g. drawer close/open).
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
      setIsEmpty(!ref.current.textContent.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    setIsEmpty(!ref.current.textContent.trim());
    onChange?.(html);
  };

  const exec = (command, arg = null) => {
    document.execCommand(command, false, arg);
    ref.current?.focus();
    emit();
  };

  const setBlock = (tag) => {
    document.execCommand("formatBlock", false, tag);
    ref.current?.focus();
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Enter URL (https://…)");
    if (!url) return;
    if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url)) {
      window.alert("Please enter a valid URL starting with http(s):// or mailto:");
      return;
    }
    exec("createLink", url);
    // Ensure link opens in new tab.
    setTimeout(() => {
      if (!ref.current) return;
      ref.current.querySelectorAll("a").forEach((a) => {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      });
      emit();
    }, 0);
  };

  return (
    <div
      className="rounded-md border border-[#E2E8F0] bg-white"
      data-testid={testId}
    >
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-[#E2E8F0] bg-[#F8FAFC] rounded-t-md">
        <TbBtn onClick={() => exec("bold")} title="Bold" icon={Bold} />
        <TbBtn onClick={() => exec("italic")} title="Italic" icon={Italic} />
        <TbBtn onClick={() => exec("underline")} title="Underline" icon={Underline} />
        <Divider />
        <TbBtn onClick={() => setBlock("H2")} title="Heading 2" icon={Heading2} />
        <TbBtn onClick={() => setBlock("H3")} title="Heading 3" icon={Heading3} />
        <TbBtn onClick={() => setBlock("BLOCKQUOTE")} title="Blockquote" icon={Quote} />
        <Divider />
        <TbBtn onClick={() => exec("insertUnorderedList")} title="Bullet list" icon={List} />
        <TbBtn onClick={() => exec("insertOrderedList")} title="Numbered list" icon={ListOrdered} />
        <Divider />
        <TbBtn onClick={addLink} title="Insert link" icon={LinkIcon} />
        <TbBtn
          onClick={() => exec("insertHorizontalRule")}
          title="Horizontal rule"
          icon={Minus}
        />
      </div>
      <div className="relative">
        {isEmpty && (
          <div
            className="pointer-events-none absolute top-3 left-3 text-[13px] text-[#94A3B8]"
            aria-hidden
          >
            {placeholder}
          </div>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          onPaste={(e) => {
            // Paste as plain text to keep formatting clean.
            e.preventDefault();
            const text = e.clipboardData.getData("text/plain");
            document.execCommand("insertText", false, text);
          }}
          className="cms-rte prose prose-sm max-w-none px-3 py-3 outline-none text-[13.5px] leading-relaxed"
          style={{ minHeight }}
          data-testid={testId ? `${testId}-editor` : undefined}
        />
      </div>
    </div>
  );
}

function TbBtn({ icon: Icon, onClick, title }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault() /* keep selection */}
      onClick={onClick}
      title={title}
      className="h-7 w-7 rounded inline-flex items-center justify-center text-[#334155] hover:bg-white hover:text-[#0F172A] transition-colors"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-4 w-px bg-[#E2E8F0]" />;
}
